import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/lib/Seo";
import { useFetch } from "@/lib/api";
import { FadeUp } from "@/lib/motion";
import Breadcrumbs from "@/components/Breadcrumbs";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const { data: catalog, loading, error } = useFetch("/catalog");
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(() => searchParams.get("category") || "All");
  const categories = useMemo(
    () => ["All", ...new Set((catalog || []).map((c) => c.category))],
    [catalog]
  );
  const shown = (catalog || []).filter((i) => category === "All" || i.category === category);

  return (
    <div className="pt-[72px]">
      <Seo
        title="Shop Personalized Wedding Details — Signage, Tablescapes & Florals | Marble & Veil"
        description="Browse personalized welcome signs, menus, place cards, styled tablescapes, fresh florals, and custom dance floor designs. Add what you love and request a quote."
        path="/shop"
      />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <Breadcrumbs trail={[["Home", "/"], ["Shop"]]} />
        <FadeUp>
          <h1 className="mt-6 font-display text-5xl leading-[1.05] text-ink sm:text-6xl">The Collection</h1>
          <p className="mt-5 max-w-xl leading-relaxed text-smoke">
            A browsable, quote-request catalog. Every piece is designed with you and priced in your
            proposal — no prices by the row, no checkout, no pressure.
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="shop-filters">
            {categories.map((c) => (
              <button key={c} data-testid={`shop-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="mv-chip" data-on={category === c} onClick={() => setCategory(c)} aria-pressed={category === c}>
                {c}
              </button>
            ))}
          </div>
        </FadeUp>

        {loading && <p className="mt-16 text-smoke" data-testid="shop-loading">Gathering the collection…</p>}
        {error && (
          <p className="mt-16 text-red-900" data-testid="shop-error" role="alert">
            The collection could not be loaded just now. Please refresh in a moment.
          </p>
        )}
        {shown.length === 0 && !loading && !error && (
          <p className="mt-16 text-smoke" data-testid="shop-empty">Nothing in this category yet — check back soon.</p>
        )}

        <div className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shown.map((item, i) => (
            <FadeUp key={item.slug} delay={(i % 4) * 0.07}>
              <ProductCard item={item} priority={i < 4} />
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
